import { StTitleIconContainer, IconWrapper } from './FAQAccordion.style';
import Accordion from '../../components/shared/Accordion/Accordion';
import { ComponentProps } from 'lib/component-props';
import { Field, ImageField } from '@sitecore-jss/sitecore-jss-nextjs';
import CollapseExpandIcon from '../../components/shared/Accordion/CollapseExpandIcon';
import AboutWrapper, { ITabLinkItem } from '../../components/AboutWrapper/AboutWrapper';
import ShareAndDownload from '../../components/ShareAndDownload/ShareAndDownload';
import UiHeading from '../shared/Heading';
import UiBox from '../shared/Box';
import Head from 'next/head';

interface AccordianItem {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    accordionTitle: Field<string>;
    accordionDescription: Field<string>;
    accordionAdditionalDescription?: Field<string>;
    autoPlayVideo: Field<boolean>;
    imageUrl?: ImageField;
    videoUrl?: {
      value?: {
        href: string;
        linktype?: string;
        url?: string;
        anchor?: string;
        target?: string;
      };
    };
  };
}

type donwloadItems = {
  value: {
    description: string;
    displayName: string;
    extension: string;
    keywords: string;
    mimeType: string;
    name: string;
    size: string;
    src: string;
    title: string;
  };
};

export type FAQAccordionProps = ComponentProps & {
  fields: {
    ShareAndDownload: {
      displayName: string;
      fields: {
        shareIcon: ImageField;
        shareText: Field<string>;
        showShareOption: Field<string>;
        sharingMedia: any;
        downloadText: Field<string>;
        downloadIcon: ImageField;
        downloadFile: ImageField;
        showDownloadOption: Field<string>;
        fileName?: donwloadItems;
      };
    };
    accordionList: Array<AccordianItem>;
    tabLinks: Array<ITabLinkItem>;
    hideExpandAllButton: Field<boolean>;
    expandAllText: Field<string>;
    expandOnlyOneAccordion: Field<boolean>;
    collapseAllText: Field<string>;
    activeTab: ITabLinkItem;
    ariaLabel: Field<string>;
    downloadFile?: any;
    sectionHeader: Field<string>;
  };
};
const FAQAccordion = (props: FAQAccordionProps): JSX.Element => {
  const { tabLinks, activeTab } = props?.fields || {};
  const accordionProps = {
    expandOnlyOneAccordion: props?.fields?.expandOnlyOneAccordion
      ? props?.fields?.expandOnlyOneAccordion?.value
      : false,
    hideExpandAllButton: props?.fields?.hideExpandAllButton
      ? props?.fields?.hideExpandAllButton?.value
      : false,
    sectionsList: props.fields?.accordionList,
    expandAllText: props?.fields?.expandAllText?.value || '',
    collapseAllText: props?.fields?.collapseAllText?.value || '',
  };

  const generateFAQSchema = () => {
    if (!props?.fields?.accordionList || props?.fields?.accordionList?.length === 0) {
      return null;
    }

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: props?.fields?.accordionList?.map((item) => ({
        '@type': 'Question',
        name: item?.fields?.accordionTitle?.value || '',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${item?.fields?.accordionDescription?.value || ''}${
            item?.fields?.accordionAdditionalDescription?.value
              ? ` ${item?.fields?.accordionAdditionalDescription?.value}`
              : ''
          }`
            .replace(/<[^>]*>/g, '')
            .trim(),
        },
      })),
    };

    return (
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqSchema),
          }}
        />
      </Head>
    );
  };

  return (
    <AboutWrapper tabLinks={tabLinks} activeTab={activeTab}>
      <>
        {generateFAQSchema()}
        <Accordion {...accordionProps}>
          {(singleLineHeading, handleExpand, showExpandIcon, showExpandIconText) => (
            <UiBox styles={{ padding: '20px 0', width: '100%' }}>
              <StTitleIconContainer
                container
                justifyContent={'space-between'}
                alignItems={'center'}
              >
                <UiHeading size="h2" data-testid="page-title" color="primary">
                  {props.fields?.sectionHeader as Field<string>}
                </UiHeading>

                {!accordionProps?.hideExpandAllButton && (
                  <IconWrapper>
                    <>
                      <ShareAndDownload
                        params={{
                          pageType: 'aboutUs',
                        }}
                        fields={{
                          ...props?.fields?.ShareAndDownload?.fields,
                          downloadFile: props?.fields?.downloadFile,
                        }}
                      />
                      <CollapseExpandIcon
                        handleClick={handleExpand}
                        singleLineHeading={singleLineHeading}
                        showExpandIcon={showExpandIcon}
                        showExpandIconText={showExpandIconText}
                        ariaLabel={props?.fields?.ariaLabel?.value}
                      />
                    </>
                  </IconWrapper>
                )}
              </StTitleIconContainer>
            </UiBox>
          )}
        </Accordion>
      </>
    </AboutWrapper>
  );
};

export default FAQAccordion;
